const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/actions/userActions.ts';
import { Dispatch } from 'redux';
import Imm from 'immutable';
import { KISAPI as API } from '../middleware/kisapi';
import * as UserDefs from '../defs/userDefs';
import { Logger } from '../services/KisLoggerService';
import { IKISAPIAction, IKISAPIMeta, KisApiAction, kisapiActionTypes } from '../defs/actionTypes';
import { SupportedLocale } from '../defs/generalDefs';
import { apiRefreshToken, apiRevokeToken } from '@cistec/common_web/actions/kisapi/oauth2';
import {
    apiCheckUserPin,
    apiGetOcodeType,
    apiLoad1UserByLoginName,
    apiLoad1UserByVisum,
    apiSetUserPin,
} from '@cistec/common_web/actions/kisapi/users';
import {
    apiInsertMetaKatPersDataRecord,
    apiLoad1MetaKatRecord,
    apiLoadMetaKatPersData,
    apiLoadMetaKatPersDataChildren,
    apiLoadUserPrefsBlock,
    apiUpdateMetaKatRecord,
    MetaKatFields,
} from '@cistec/common_web/actions/kisapi/metaKat';
import { apiCheckAccessCodes, apiLoadAccessValues } from '@cistec/common_web/actions/kisapi/security';

const Log = Logger('userActions');

export const LOGOUT = 'LOGOUT';
export const LOCK = 'LOCK';
export const INVALIDATE_AUTHENTICATION = 'INVALIDATE_AUTHENTICATION';
export const RESET_STATE = 'RESET_STATE';
export const REAUTHENTICATE = 'REAUTHENTICATE';
export const SET_USERNAMEISVISUM = 'SET_USERNAMEISVISUM';
export const TRIGGER_BIOMETRIC_SCAN = 'TRIGGER_BIOMETRIC_SCAN';

export const GET_USER_DATA = kisapiActionTypes('GET_USER_DATA');
export const GET_USER_PERS_DATA = kisapiActionTypes('GET_USER_PERS_DATA');
export const GET_USER_PREFS = kisapiActionTypes('GET_USER_PREFS');
export const GET_OCODE_TYPE = kisapiActionTypes('GET_OCODE_TYPE');
export const REFRESH_TOKEN = kisapiActionTypes('REFRESH_TOKEN');
export const REVOKE_TOKEN = kisapiActionTypes('REVOKE_TOKEN');
export const LOAD_PERSMOBILE_CHILDREN = kisapiActionTypes('LOAD_PERSMOBILE_CHILDREN');
export const LOAD_PERSDATA_RECORD = kisapiActionTypes('LOAD_PERSDATA_RECORD');
export const SAVE_PERSMOBILE_RECORD = kisapiActionTypes('SAVE_PERSMOBILE_RECORD');
export const LOAD_USER_RIGHTS = kisapiActionTypes('LOAD_USER_RIGHTS');
export const LOAD_USER_ACCESSVALUES = kisapiActionTypes('LOAD_USER_ACCESSVALUES');
export const SET_USERPIN = kisapiActionTypes('SET_USERPIN');
export const CHECK_USERPIN = kisapiActionTypes('CHECK_USERPIN');
export const LOAD_PERSCALS_CHILDREN = kisapiActionTypes('LOAD_PERSCALS_CHILDREN');
export const LOAD_PERSPREFS_CHILDREN = kisapiActionTypes('LOAD_PERSPREFS_CHILDREN');
export const BIOMETRIC_SCAN = kisapiActionTypes('BIOMETRIC_SCAN');
export const FETCH_FEATURE_FLAGS = kisapiActionTypes('FETCH_FEATURE_FLAGS');

export interface ILoadPersMobileChildrenMeta extends IKISAPIMeta {
    visum: string;
}

export interface ILoadPersMobileChildrenAction extends IKISAPIAction {
    meta: ILoadPersMobileChildrenMeta;
}

export interface ILoadPersMobileRecordMeta extends IKISAPIMeta {
    branch: string;
}

export interface ILoadPersMobileRecordAction extends IKISAPIAction {
    meta: ILoadPersMobileRecordMeta;
}

export interface ISavePersMobileRecordMeta extends IKISAPIMeta {
    branch: string;
}

export interface ISavePersMobileRecordAction extends IKISAPIAction {
    meta: ISavePersMobileRecordMeta;
}

export interface ICheckUserPinMeta extends IKISAPIMeta {
    visum: string;
    pin: string;
}

export interface ICheckUserPinAction extends IKISAPIAction {
    meta: ICheckUserPinMeta;
}

export interface IRefreshTokenMeta extends IKISAPIMeta {}

export interface IRefreshTokenAction extends IKISAPIAction {
    meta: IRefreshTokenMeta;
}

export interface LoadPersDataChildrenMeta extends IKISAPIMeta {
    visum: string;
}

export interface LoadPersDataChildrenAction extends IKISAPIAction {
    meta: LoadPersDataChildrenMeta;
}

export function triggerBiometricScan() {
    return {
        type: TRIGGER_BIOMETRIC_SCAN,
    };
}

export function triggerClearAppStorage() {
    return {
        type: 'TRIGGER_CLEAR_APP_STORAGE',
    };
}

export function logoutUser() {
    return { type: LOGOUT, payload: {} };
}

export function invalidateAuthentication() {
    return { type: INVALIDATE_AUTHENTICATION, payload: {} };
}

export function resetState() {
    return {
        type: RESET_STATE,
        payload: {},
    };
}

export function refreshOAuthToken(token: string) {
    return {
        type: REFRESH_TOKEN.combined,
        [API]: apiRefreshToken(token),
        meta: { suppressErrorMsg: true },
    };
}

export function reauthenticateUser() {
    return { type: REAUTHENTICATE, payload: {} };
}

export const actionLock = () => {
    return { type: LOCK, payload: {} };
};

export function revokeToken(): any {
    return (dispatch: any, getState: any) => {
        const tokens: string[] = [
            getState().user.getIn(['apiToken', 'access_token']),
            getState().user.getIn(['apiToken', 'refresh_token']),
        ];
        const actionTypes = REVOKE_TOKEN.combined;
        tokens.forEach(token => {
            dispatch({
                type: actionTypes,
                [API]: apiRevokeToken(token),
                meta: { suppressErrorMsg: true },
            });
        });
    };
}

export function getUserRecordByLoginName(loginName: string) {
    return {
        type: GET_USER_DATA.combined,
        [API]: apiLoad1UserByLoginName(loginName),
        meta: { loginName },
    };
}

type GetUserRecordByLoginNameAction = KisApiAction<typeof getUserRecordByLoginName>;

export function getUserRecordByVisum(visum: string) {
    return {
        type: GET_USER_DATA.combined,
        [API]: apiLoad1UserByVisum(visum),
        meta: { visum },
    };
}

type GetUserRecordByVisumAction = KisApiAction<typeof getUserRecordByVisum>;

export type GetUserDataAction = GetUserRecordByLoginNameAction | GetUserRecordByVisumAction;

export function setUsernameIsVisum(usernameIsVisum: boolean) {
    return {
        type: SET_USERNAMEISVISUM,
        payload: {
            usernameIsVisum,
        },
    };
}

export function actionSetUserPIN(pin: string) {
    return {
        type: SET_USERPIN.combined,
        [API]: apiSetUserPin(pin),
    };
}

export function checkUserPin(visum: string, pin: string) {
    const meta: ICheckUserPinMeta = {
        visum,
        pin,
        suppressErrorMsg: true,
    };
    return {
        meta,
        type: CHECK_USERPIN.combined,
        [API]: apiCheckUserPin(visum, pin),
    };
}

export function actionGetUserPersData(username: string) {
    return {
        type: GET_USER_PERS_DATA.combined,
        [API]: apiLoadMetaKatPersData(username),
        meta: { suppressErrorMsg: false },
    };
}

export function actionGetUserPrefs(icode: string) {
    return {
        type: GET_USER_PREFS.combined,
        [API]: apiLoadUserPrefsBlock(icode),
        meta: { refresh: false },
    };
}

export function fetchFeatureFlags(iCode: string) {
    return {
        type: FETCH_FEATURE_FLAGS.combined,
        [API]: apiLoad1MetaKatRecord(iCode, ['ICODE', 'CONTENT'], true),
        meta: {},
    };
}

/**
 * Action to call KISAPI to load the immediate children of the user's PersMobile MetaKat subtree.
 * @param visum The visum of the user whose PersMobile data is to be loaded
 */
export function loadPersMobileChildren(visum: string) {
    const meta: ILoadPersMobileChildrenMeta = {
        visum,
        suppressErrorMsg: true, // because the reply will be 404 if the PersMobile tree doesn't exist yet
        showDlgWhenResultHasMoreRows: true,
    };
    return {
        meta,
        type: LOAD_PERSMOBILE_CHILDREN.combined,
        [API]: apiLoadMetaKatPersDataChildren('PersMobile', visum, ['ICODE', 'PCODE', 'NAME', 'KAT', 'FKEY']),
    };
}

/**
 * Action to call KISAPI to load the contents of a particular record of the user's PersMobile data. Note that you must
 * call loadPersMobileChildren before this, to retrieve the record's ICODE.
 * @param branch The key of an existing branch in state.ui.get ('persdata')
 */
export function loadPersDataRecord(branch: string): any {
    // Using thunk middleware.
    return (dispatch: Dispatch<any>, getState: () => any) => {
        const visum: string = getState().user.getIn(['record', 'VISUM']);

        if (!visum) {
            Log.warn('loadPersDataRecord: Cannot determine visum! Not logged in?');
        } else {
            const rec = getState().user.getIn(['persdata', branch]);

            if (!rec) {
                // The branch is expected to exist, even if it is empty.
                Log.warn(
                    'loadPersDataRecord: There is no subtree named ' +
                        `${branch} in the current state! Did you misspell it?`
                );
            } else {
                const icode: string = rec.get('ICODE');

                if (!icode) {
                    // The record's ICODE is not known. This either means you
                    // forgot to call loadPersMobileChildren, or that the user
                    // does not have a record for this branch yet.
                    Log.info(`loadPersMobileRecord: Skipped branch ${branch}, ` + 'because the ICODE is not known.');
                } else {
                    // OK, load the MetaKat record.

                    Log.info(`loadPersMobileRecord: Loading record of branch ${branch} (${icode})`);

                    const meta: ILoadPersMobileRecordMeta = {
                        branch,
                    };

                    const action = {
                        meta,
                        type: LOAD_PERSDATA_RECORD.combined,
                        [API]: apiLoad1MetaKatRecord(icode, MetaKatFields.ALL_INCLUDING_CONTENT),
                    };

                    dispatch(action);
                }
            }
        }
    };
}

/**
 * Action to call KISAPI to store the contents of a particular record of the
 * user's PersMobile data back into the MetaKat.
 * @param branch The key of an existing branch in state.ui.get('persdata')
 * @param newLocale If defined, the preferred language will directly be updated
 */
export function savePersMobileRecord(branch: string, newLocale: SupportedLocale | null = null) {
    // Using thunk middleware.
    return (dispatch: Dispatch<any>, getState: () => any) => {
        const visum: string = getState().user.getIn(['record', 'VISUM']);

        if (!visum) {
            Log.warn('savePersMobileRecord: Cannot determine visum! Not logged in?');
            // FIXME: not totally sure yet how to fix this, but this work around seems necessary
            // for sagas to be able to take the corresponding action directly after a put of this thunk.
            // see logout saga for example.
            // This represents just an edge case, but can happen if from the re-login page, the sign out is
            // called without a valid visum present. logout saga would get stuck in the middle
            setTimeout(() => dispatch({ type: SAVE_PERSMOBILE_RECORD.failure }), 0);
        } else {
            let rec = getState().user.getIn(['persdata', branch]);

            if (!rec) {
                // The branch is expected to exist, even if it is empty.
                Log.warn(
                    'savePersMobileRecord: There is no subtree named ' +
                        `${branch}' in the current state! Did you misspell it?`
                );
            } else {
                if (newLocale) {
                    if (branch !== 'prefs') {
                        throw new Error("newLocale may only be set with branch 'prefs'");
                    }
                    rec = rec.setIn(['CONTENT', 'locale'], newLocale);
                }

                const meta: ISavePersMobileRecordMeta = {
                    branch,
                };

                const etag: string = rec.get('ETAG');

                if (etag) {
                    // ETAG present: Update the existing record!

                    Log.info(`savePersMobileRecord ('${branch}'): Updating record:`, rec);

                    const action = {
                        type: SAVE_PERSMOBILE_RECORD.combined,
                        [API]: apiUpdateMetaKatRecord(rec),
                        meta,
                    };

                    dispatch(action);
                } else {
                    // No ETAG: Create a new record!

                    rec = Imm.fromJS({
                        KAT: 'Group',
                        FKEY: branch, // KAT/FKEY identifies the record
                        CONTENT: rec.get('CONTENT'),
                    });

                    Log.info(`savePersMobileRecord ('${branch}'): Creating new record:`, rec);

                    // FIXME: A parallel session may have created the entry
                    // already! The following code will always generate a new
                    // entry, thus possibly rendering the KAT/FKEY non-unique!
                    // KISAPI should enforce the constraint! FIXME

                    const action = {
                        meta,
                        type: SAVE_PERSMOBILE_RECORD.combined,
                        [API]: apiInsertMetaKatPersDataRecord('PersMobile', visum, rec),
                    };

                    dispatch(action);
                }
            }
        }
    };
}

/**
 * Action to cause KISAPI to evaluate a list of access codes against the current user. The caller
 * is responsible for calling AccessRightsUtils.translateAccessCode before invoking this action. It
 * is an error to invoke this action with the special access codes ANYBODY, NOBODY or
 * PARENT_RIGHTS, because we shouldn't bother KISAPI when evaluating these, and NOBODY and
 * PARENT_RIGHTS cannot be put into the store anyway, because the empty string is not a valid key.
 * @param accessCodes array
 * @param callerId optional identifier that may be used to identify the action via meta from sagas, etc.
 */
export function actionLoadUserRights(accessCodes: string[], callerId?: string) {
    if (!accessCodes || !accessCodes.length) {
        throw new Error('Do not call the action with an empty list of access codes!');
    }

    if (accessCodes.indexOf(':') >= 0 || accessCodes.indexOf('|') >= 0) {
        throw new Error('You must translate extended access codes before calling the action!');
    }

    accessCodes.forEach(code => {
        if (code === UserDefs.ANYBODY || code === UserDefs.NOBODY || code === UserDefs.PARENT_RIGHTS) {
            throw new Error('The list contains access codes that must be handled differently.');
        }
    });

    return {
        type: LOAD_USER_RIGHTS.combined,
        [API]: apiCheckAccessCodes(accessCodes),
        meta: { accessCodes, callerId },
    };
}

export type LoadUserRightsAction = KisApiAction<typeof actionLoadUserRights>;

/**
 * Action to load a list of access values.
 * @param accessValues The names of the access values to load
 * @param treatAccessCodeAsAccessValue true = fall back to loading an access code if the access value does not exist.
 *    Setting this flag to true is not recommended, because on the one hand, the access code will be stored as an
 *    access value in the state, while on the other hand, ambiguous situations can arise, because the frontend cannot
 *    tell if a value of '0' or '1' was resolved as an access value or access code. Please use loadUserRights if you
 *    mean to load access codes.
 * @param callerId Optional meta value to identify this action
 */
export function actionLoadAccessValues(
    accessValues: string[],
    treatAccessCodeAsAccessValue: boolean = false,
    callerId?: string
) {
    return {
        type: LOAD_USER_ACCESSVALUES.combined,
        [API]: apiLoadAccessValues(accessValues, treatAccessCodeAsAccessValue),
        meta: { accessValues, treatAccessCodeAsAccessValue, callerId },
    };
}

export type LoadAccessValuesAction = KisApiAction<typeof actionLoadAccessValues>;

/**
 * Action to call KISAPI to load the immediate children of one of the user's PersData subtree.
 * @param visum (in): The visum of the user whose PersData records are to be loaded.
 */
export function loadPersCalsChildren(visum: string) {
    const meta: LoadPersDataChildrenMeta = {
        visum,
        suppressErrorMsg: true, // because we'll get a 404 when the user has no favorites
        showDlgWhenResultHasMoreRows: true,
    };
    return {
        type: LOAD_PERSCALS_CHILDREN.combined,
        [API]: apiLoadMetaKatPersDataChildren('PersCals', visum, ['ICODE', 'PCODE', 'REF', 'KAT', 'FKEY', 'NAME']),
        meta,
    };
}

/**
 * Action to call KISAPI to load the immediate children of one of the user's PersData subtree.
 * @param visum (in): The visum of the user whose PersData records are to be loaded.
 */
export function loadPersPrefsChildren(visum: string) {
    const meta: LoadPersDataChildrenMeta = {
        visum,
        suppressErrorMsg: true, // because we'll get a 404 when the user has no favorites
        showDlgWhenResultHasMoreRows: true,
    };
    return {
        type: LOAD_PERSPREFS_CHILDREN.combined,
        [API]: apiLoadMetaKatPersDataChildren('PersPrefs', visum, ['ICODE', 'PCODE', 'REF', 'KAT', 'FKEY', 'NAME']),
        meta,
    };
}

/**
 * Action to get clinic and station out of OCODE, since OCODE can either be clinic or station
 * @param ocode (in)
 */
export function getOcodeType(ocode: string) {
    return {
        type: GET_OCODE_TYPE.combined,
        [API]: apiGetOcodeType(ocode),
        meta: { ocode },
    };
}
